import { Table } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";
import getColumnConfig from "./getColumnsConfig";

const TeamStrategiesTable = ({
  data = [],
  tableConfig = {},
  initialTableState = {},
  rowFilters,
  highlightedObjectiveId,
}) => {
  const tableRef = useRef(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    // Only scroll on initial load, not on subsequent updates
    if (highlightedObjectiveId && !hasScrolledRef.current && tableRef.current) {
      // Small delay to ensure the table is fully rendered and expanded
      const scrollTimer = setTimeout(() => {
        const highlightedRow = tableRef.current.querySelector(
          `[data-objective-id="${highlightedObjectiveId}"]`
        );

        if (highlightedRow) {
          highlightedRow.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          hasScrolledRef.current = true;
        }
      }, 500);

      return () => clearTimeout(scrollTimer);
    }

    return undefined;
  }, [highlightedObjectiveId]);

  const columns = getColumnConfig();
  const muiTableBodyCellProps = useCallback(
    ({ row }) => ({
      "data-objective-id": row.original.id,
      sx: () => ({
        "& .name-col": {
          textWrap: "wrap",
        },
        color: !row.originalSubRows ? "grey" : "",
        fontWeight: row.depth === 0 ? 600 : row.depth === 1 ? 500 : undefined,
        backgroundColor:
          highlightedObjectiveId && row.original.id === highlightedObjectiveId
            ? "#e3f2fd"
            : "transparent",
      }),
    }),
    [highlightedObjectiveId]
  );

  return (
    <div ref={tableRef}>
      <Table
        data={data}
        columns={columns}
        enableExpanding
        enableExpandAll
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enablePagination={false}
        getSubRows={rowFilters}
        muiTableBodyCellProps={muiTableBodyCellProps}
        {...tableConfig}
        initialState={{
          ...initialTableState,
        }}
        defaultColumn={{
          // NOTE: Don't change this. These are just explicit defaults for clarity.
          // Read the docs in ColumnConfiguration.md for more info.
          grow: 0,
          size: 0,
          minSize: 0,
          maxSize: Number.MAX_SAFE_INTEGER,
        }}
        layoutMode="grid-no-grow"
        // required styling for <tr> to vert-center header text when using grid-no-grow (i.e. display:grid)
        muiTableHeadRowProps={() => ({
          sx: {
            alignItems: "center",
          },
        })}
      />
    </div>
  );
};

TeamStrategiesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  tableConfig: PropTypes.object,
  initialTableState: PropTypes.object,
  rowFilters: PropTypes.func,
  highlightedObjectiveId: PropTypes.string,
};

export default TeamStrategiesTable;
